import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import { createPlayerScore, updatePlayerScore, getAwardCategories, assignPlayerAward } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { supabase } from '../lib/supabase';

type HoleStrokesMap = Record<number, string>;

const DEFAULT_PARS: Record<number, number> = {
  1: 4, 2: 4, 3: 3, 4: 5, 5: 4, 6: 3, 7: 4, 8: 5, 9: 4,
  10: 4, 11: 3, 12: 5, 13: 4, 14: 4, 15: 3, 16: 5, 17: 4, 18: 4,
};

// Define the base schema for player score data (without refinement)
const basePlayerScoreSchema = z.object({
  player_id: z.string().uuid(),
  tournament_id: z.string().uuid(),
  round: z.number().int().positive(),
  score: z.number().int().optional(),
  current_hole: z.number().int().min(1).max(18).optional().nullable(),
  date: z.string().min(1, { message: "Date is required" }),
  notes: z.string().optional(),
  category: z.string().min(1, { message: "Category is required" }),
  player_status: z.enum(['PLAYED', 'DNP', 'WD', 'DQ']).default('PLAYED')
});

// Define the schema for award assignment
const awardAssignmentSchema = z.object({
  award_id: z.string().optional(),
  award_notes: z.string().optional()
});

// Combined schema for the form with refinement
const combinedSchema = basePlayerScoreSchema.merge(awardAssignmentSchema).refine((data) => {
  // If status is PLAYED, score must be provided
  if (data.player_status === 'PLAYED' && (data.score === undefined || data.score === null)) {
    return false;
  }
  // If status is not PLAYED, score must not be provided
  if (data.player_status !== 'PLAYED' && data.score !== undefined && data.score !== null) {
    return false;
  }
  return true;
}, {
  message: "Score is required for PLAYED status, and must be empty for DNP/WD/DQ",
  path: ["score"]
});

type CombinedFormData = z.infer<typeof combinedSchema>;

interface PlayerScoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  score: {
    id: string;
    player_id: string;
    tournament_id: string;
    round: number;
    score?: number | null;
    current_hole?: number | null;
    date: string;
    notes?: string;
    category?: string;
    player_status?: 'PLAYED' | 'DNP' | 'WD' | 'DQ';
  } | null;
  playerId: string;
  tournamentId: string;
  onSuccess: () => void;
  existingRounds?: number[];
}

export default function PlayerScoreModal({
  isOpen,
  onClose,
  score,
  playerId,
  tournamentId,
  onSuccess,
  existingRounds = []
}: PlayerScoreModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [playerCategory, setPlayerCategory] = useState('');
  const [awardCategories, setAwardCategories] = useState<Array<{id: string, name: string, description: string}>>([]);
  const [playerAwards, setPlayerAwards] = useState<Array<{id: string, award_id: string, notes: string}>>([]);
  const [selectedAwardId, setSelectedAwardId] = useState<string>('');
  const [awardNotes, setAwardNotes] = useState<string>('');
  const [playerStatus, setPlayerStatus] = useState<'PLAYED' | 'DNP' | 'WD' | 'DQ'>('PLAYED');
  const [holeStrokes, setHoleStrokes] = useState<HoleStrokesMap>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<CombinedFormData>({
    resolver: zodResolver(combinedSchema),
    defaultValues: {
      player_id: playerId,
      tournament_id: tournamentId,
      round: 1,
      score: 0,
      current_hole: null,
      date: new Date().toISOString().split('T')[0],
      notes: '',
      category: '',
      player_status: 'PLAYED',
      award_id: undefined,
      award_notes: ''
    }
  });

  const watchPlayerStatus = watch('player_status');

  useEffect(() => {
    if (playerId) {
      fetchPlayerCategory();
      fetchAwardCategories();
      fetchPlayerAwards();
    }
  }, [playerId]);

  const fetchHoleScores = async (round: number) => {
    if (!playerId || !tournamentId) return;
    const { data } = await supabase
      .from('hole_scores')
      .select('hole, strokes')
      .eq('player_id', playerId)
      .eq('tournament_id', tournamentId)
      .eq('round', round);
    if (data) {
      const map: HoleStrokesMap = {};
      data.forEach((r) => { map[r.hole] = String(r.strokes); });
      setHoleStrokes(map);
    } else {
      setHoleStrokes({});
    }
  };

  const fetchAwardCategories = async () => {
    try {
      const { data, error } = await getAwardCategories();
      if (error) throw error;
      
      if (data) {
        setAwardCategories(data);
      }
    } catch (err) {
      console.error('Error fetching award categories:', err);
    }
  };

  const fetchPlayerAwards = async () => {
    try {
      const { data, error } = await supabase
        .from('player_awards')
        .select('*')
        .eq('player_id', playerId)
        .eq('tournament_id', tournamentId);

      if (error) throw error;
      
      if (data) {
        setPlayerAwards(data);
        
        // Reset award selection fields - we'll handle them separately
        setValue('award_id', '');
        setValue('award_notes', '');
      }
    } catch (err) {
      console.error('Error fetching player awards:', err);
    }
  };

  const fetchPlayerCategory = async () => {
    try {
      const { data, error } = await supabase
        .from('players')
        .select('category')
        .eq('id', playerId)
        .single();

      if (error) throw error;
      
      if (data) {
        setPlayerCategory(data.category);
        setValue('category', data.category);
      }
    } catch (err) {
      console.error('Error fetching player category:', err);
    }
  };

  useEffect(() => {
    if (score) {
      setValue('player_id', score.player_id);
      setValue('tournament_id', score.tournament_id);
      setValue('round', score.round);
      setValue('score', score.score || undefined);
      setValue('current_hole', score.current_hole ?? null);
      setValue('date', score.date);
      setValue('notes', score.notes || '');
      setValue('category', score.category || playerCategory);
      setValue('player_status', score.player_status || 'PLAYED');
      setPlayerStatus(score.player_status || 'PLAYED');
      fetchHoleScores(score.round);
    } else {
      reset({
        player_id: playerId,
        tournament_id: tournamentId,
        round: 1,
        score: 0,
        current_hole: null,
        date: new Date().toISOString().split('T')[0],
        notes: '',
        category: playerCategory,
        player_status: 'PLAYED'
      });
      setPlayerStatus('PLAYED');
      setHoleStrokes({});
      setValue('award_id', undefined);
      setValue('award_notes', '');
    }
  }, [score, playerId, tournamentId, reset, setValue, playerCategory]);

  const onSubmit = async (data: CombinedFormData) => {
    try {
      setIsSubmitting(true);
      setError('');
      
      const scoreData = {
        player_id: data.player_id,
        tournament_id: data.tournament_id,
        round: data.round,
        score: data.player_status === 'PLAYED' ? data.score : null,
        current_hole: data.current_hole ?? null,
        date: data.date,
        notes: data.notes,
        player_status: data.player_status
      };

      if (score) {
        const { error: updateError } = await updatePlayerScore(score.id, scoreData);
        if (updateError) throw updateError;
        toast.success('Score updated successfully');
      } else {
        const { error: createError } = await createPlayerScore(scoreData);
        if (createError) throw createError;
        toast.success('Score added successfully');

        if (!data.award_id && score) {
          const { error: removeError } = await supabase
            .from('player_awards')
            .delete()
            .eq('player_id', data.player_id)
            .eq('tournament_id', data.tournament_id);
          if (removeError) console.error('Error removing player award:', removeError);
          else toast.success('Award removed successfully');
        }
      }

      if (data.player_status === 'PLAYED') {
        const holeRows = Object.entries(holeStrokes)
          .filter(([, v]) => v !== '' && !isNaN(Number(v)) && Number(v) >= 1)
          .map(([hole, strokes]) => ({
            player_id: data.player_id,
            tournament_id: data.tournament_id,
            round: data.round,
            hole: Number(hole),
            strokes: Number(strokes),
            par: DEFAULT_PARS[Number(hole)] ?? null,
          }));

        if (holeRows.length > 0) {
          const { error: holeErr } = await supabase
            .from('hole_scores')
            .upsert(holeRows, { onConflict: 'player_id,tournament_id,round,hole' });
          if (holeErr) console.error('Error saving hole scores:', holeErr);
        }
      }

      // Update player category if it has changed
      if (data.category !== playerCategory) {
        const { error: updateCategoryError } = await supabase
          .from('players')
          .update({ category: data.category })
          .eq('id', playerId);
          
        if (updateCategoryError) {
          console.error('Error updating player category:', updateCategoryError);
          // Continue anyway, just log the error
        } else {
          toast.success('Player category updated successfully');
        }
      }

      // Handle award assignment (or removal if no award is selected)
      const { error: awardError } = await assignPlayerAward({
        player_id: data.player_id,
        tournament_id: data.tournament_id,
        award_id: data.award_id,
        notes: data.award_notes || ''
      });
      
      if (awardError) {
        console.error('Error managing award:', awardError);
        toast.error('Failed to update award');
      } else if (data.award_id && data.award_id.trim() !== '') {
        toast.success('Award assigned successfully');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Score submission error:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if a round already has a score for this player
  const roundHasScore = (round: number) => {
    return existingRounds.includes(round);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="border-b border-gray-200 bg-golf-sand/30 px-4 py-5 sm:px-6">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {score ? 'Edit Player Score' : 'Add Player Score'}
                    </Dialog.Title>
                  </div>

                  <div className="px-4 py-5 sm:p-6 space-y-6">
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-600">
                        <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                        <p className="text-sm">{error}</p>
                      </div>
                    )}

                    <input
                      type="hidden"
                      {...register('player_id')}
                      value={playerId}
                    />

                    <input
                      type="hidden"
                      {...register('tournament_id')}
                      value={tournamentId}
                    />

                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="round" className="block text-sm font-medium text-gray-700">
                          Round <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1 relative">
                          <select
                            {...register('round', { valueAsNumber: true })}
                            className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm ${
                              score ? 'bg-gray-100' : ''
                            }`}
                            disabled={!!score}
                            onChange={(e) => {
                              setValue('round', Number(e.target.value));
                              fetchHoleScores(Number(e.target.value));
                            }}
                          >
                            {[1, 2, 3, 4].map((roundNum) => (
                              <option 
                                key={roundNum} 
                                value={roundNum}
                                disabled={!score && roundHasScore(roundNum)} // Disable if round already has score (only for new scores)
                              >
                                Round {roundNum} {!score && roundHasScore(roundNum) ? '(Already recorded)' : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        {errors.round && (
                          <p className="mt-2 text-sm text-red-600">{errors.round.message}</p>
                        )}
                        {!score && existingRounds.length > 0 && (
                          <p className="mt-1 text-xs text-gray-500">
                            This player already has scores for rounds: {existingRounds.join(', ')}
                          </p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="player_status" className="block text-sm font-medium text-gray-700">
                          Player Status <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <select
                            {...register('player_status')}
                            onChange={(e) => {
                              setValue('player_status', e.target.value as any);
                              setPlayerStatus(e.target.value as any);
                              // Clear score if status is not PLAYED
                              if (e.target.value !== 'PLAYED') {
                                setValue('score', undefined);
                              }
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          >
                            <option value="PLAYED">Played</option>
                            <option value="DNP">Did Not Play (DNP)</option>
                            <option value="WD">Withdrawn (WD)</option>
                            <option value="DQ">Disqualified (DQ)</option>
                          </select>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          Select player status for this round
                        </p>
                        {errors.player_status && (
                          <p className="mt-2 text-sm text-red-600">{errors.player_status.message}</p>
                        )}
                      </div>

                      {watchPlayerStatus === 'PLAYED' && (
                        <>
                          <div className="sm:col-span-3">
                            <label htmlFor="score" className="block text-sm font-medium text-gray-700">
                              Score <span className="text-red-500">*</span>
                            </label>
                            <div className="mt-1">
                              <input
                                type="number"
                                {...register('score', { valueAsNumber: true })}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                              />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Enter score relative to par (e.g., -2 for 2 under par, 0 for even par, +3 for 3 over par)
                            </p>
                            {errors.score && (
                              <p className="mt-2 text-sm text-red-600">{errors.score.message}</p>
                            )}
                          </div>

                          <div className="sm:col-span-3">
                            <label htmlFor="current_hole" className="block text-sm font-medium text-gray-700">
                              Current Hole
                            </label>
                            <div className="mt-1">
                              <select
                                {...register('current_hole', { setValueAs: (v) => v === '' ? null : Number(v) })}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                              >
                                <option value="">Finished / Not started</option>
                                {Array.from({ length: 18 }, (_, i) => i + 1).map((h) => (
                                  <option key={h} value={h}>Hole {h}</option>
                                ))}
                              </select>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              Hole the player is currently on (shown live on TV)
                            </p>
                          </div>

                          <div className="sm:col-span-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Hole-by-Hole Strokes
                              <span className="ml-2 text-xs font-normal text-gray-400">(actual strokes per hole)</span>
                            </label>
                            <div className="grid grid-cols-9 gap-1.5">
                              {Array.from({ length: 18 }, (_, i) => i + 1).map((hole) => (
                                <div key={hole} className="flex flex-col items-center gap-0.5">
                                  <span className="text-[10px] font-semibold text-gray-400">{hole}</span>
                                  <input
                                    type="number"
                                    min={1}
                                    max={15}
                                    value={holeStrokes[hole] ?? ''}
                                    onChange={(e) => setHoleStrokes((prev) => ({ ...prev, [hole]: e.target.value }))}
                                    className="w-full text-center text-sm rounded border border-gray-300 py-1 px-0 focus:border-golf-orange focus:ring-golf-orange focus:ring-1 focus:outline-none"
                                    placeholder="-"
                                  />
                                  <span className="text-[9px] text-gray-300">p{DEFAULT_PARS[hole]}</span>
                                </div>
                              ))}
                            </div>
                            <p className="mt-1.5 text-xs text-gray-400">Leave blank for holes not yet played</p>
                          </div>
                        </>
                      )}

                      {watchPlayerStatus !== 'PLAYED' && (
                        <div className="sm:col-span-3">
                          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-800">
                              {watchPlayerStatus === 'DNP' && 'Player did not participate in this round.'}
                              {watchPlayerStatus === 'WD' && 'Player withdrew from this round.'}
                              {watchPlayerStatus === 'DQ' && 'Player was disqualified from this round.'}
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="sm:col-span-3">
                        <label htmlFor="award_id" className="block text-sm font-medium text-gray-700">
                          Award Category
                        </label>
                        <div className="mt-1">
                          <select
                            {...register('award_id')}
                            value={selectedAwardId}
                            onChange={(e) => {
                              setSelectedAwardId(e.target.value);
                              setValue('award_id', e.target.value);
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          >
                            <option value="">No Award</option>
                            {awardCategories.map(award => (
                              <option key={award.id} value={award.id}>
                                {award.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {errors.award_id && (
                          <p className="mt-2 text-sm text-red-600">{errors.award_id.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          Assign a special award to this player
                          <br />
                          <span className="text-blue-600">Note: Players can receive multiple awards</span>
                        </p>
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="date"
                            {...register('date')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.date && (
                          <p className="mt-2 text-sm text-red-600">{errors.date.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          Player Category <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <select
                            {...register('category')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          >
                            <option value="">Select category</option>
                            <option value="Professional">Professional</option>
                            <option value="Amateur">Amateur</option>
                            <option value="Senior">Senior</option>
                            <option value="Junior">Junior</option>
                          </select>
                        </div>
                        {errors.category && (
                          <p className="mt-2 text-sm text-red-600">{errors.category.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                          Changing this will update the player's category
                        </p>
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                          Notes
                        </label>
                        <div className="mt-1">
                          <textarea
                            rows={3}
                            {...register('notes')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                            placeholder="Additional notes about the round"
                          />
                        </div>
                        {errors.notes && (
                          <p className="mt-2 text-sm text-red-600">{errors.notes.message}</p>
                        )}
                      </div>
                      
                      <div className="sm:col-span-6">
                        <label htmlFor="award_notes" className="block text-sm font-medium text-gray-700">
                          Award Notes
                        </label>
                        <div className="mt-1">
                          <textarea
                            rows={2}
                            {...register('award_notes')}
                            value={awardNotes}
                            onChange={(e) => {
                              setAwardNotes(e.target.value);
                              setValue('award_notes', e.target.value);
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                            placeholder="Additional notes about the award (optional)"
                          />
                        </div>
                        {errors.award_notes && (
                          <p className="mt-2 text-sm text-red-600">{errors.award_notes.message}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Current Awards Section */}
                    {playerAwards.length > 0 && (
                      <div className="sm:col-span-6 mt-4 pt-4 border-t border-gray-200">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">Current Awards</h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="space-y-2">
                            {playerAwards.map((award, index) => (
                              <div key={index} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                <div>
                                  <span className="font-medium text-gray-700">
                                    {awardCategories.find(a => a.id === award.award_id)?.name || 'Unknown Award'}
                                  </span>
                                  {award.notes && (
                                    <p className="text-xs text-gray-500 mt-1">{award.notes}</p>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const { error } = await supabase
                                        .from('player_awards')
                                        .delete()
                                        .eq('id', award.id);
                                        
                                      if (error) throw error;
                                      
                                      // Refresh awards
                                      fetchPlayerAwards();
                                      toast.success('Award removed successfully');
                                    } catch (err) {
                                      console.error('Error removing award:', err);
                                      toast.error('Failed to remove award');
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800 p-1"
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-golf-orange px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-golf-orange/90 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Saving...' : score ? 'Update Score' : 'Add Score'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}